import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import { useSession } from "users/__session/session-context";
import EntityMember from "_shared/entity-members/entity-member";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import Individual from "marketplace/individuals/individual";
import { useSelector } from "react-redux";
import { getIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";

const MainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));
const TeamList = styled(List)(({ theme }) => ({
  margin: 0,
  marginTop: 0,
  minWidth: theme.spacing(40),
  paddingBottom: theme.spacing(3),
  "&:first-of-type": {
    marginRight: theme.spacing(3),
  },
  [theme.breakpoints.down("lg")]: {
    "&:first-of-type": {
      marginRight: 0,
    },
  },
}));
const TeamListSubheader = styled(ListSubheader)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.common.white,
  color: theme.palette.common.black,
  display: "flex",
  fontSize: "1.3em",
  justifyContent: "space-between",
  padding: 0,
  "& > button": {
    height: "fit-content",
    width: "fit-content",
  },
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.3em",
  paddingTop: theme.spacing(1),
}));
const TeamMemberLink = styled(Link)(() => ({
  cursor: "pointer",
}));

type ViewVendorTeamProps = {
  className?: string;
  leader?: EntityMember;
  members?: EntityMember[];
};

export default function ViewVendorTeam(props: Readonly<ViewVendorTeamProps>) {
  const { className, leader, members } = props;

  const { openDialog } = useDialog();
  const session = useSession();

  const memberUserIds = members?.map((m) => m.userId).filter((id): id is Guid => id !== undefined) ?? [];
  const leaderIndividual = useSelector(getIndividualsByUserIds(leader?.userId ? [leader.userId] : []))[0];
  const memberIndividuals = useSelector(getIndividualsByUserIds(memberUserIds));

  /**
   * Opens the user profile dialog for the given individual
   *
   * @param individualId The id of the individual to open the dialog for
   */
  function openUserProfileDialog(individualId: Guid) {
    openDialog({
      component: <ViewIndividualProfile individualId={individualId} />,
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  /**
   * Renders a single vendor-team member's info as a list item
   *
   * @param member The member's info to render
   */
  function renderMember(member: Individual) {
    return (
      <ListItem key={member.userId.value ?? Guid.generate().value} disableGutters>
        <ListItemAvatar>
          <IndividualAvatar individual={member} session={session} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography>
              <TeamMemberLink onClick={() => openUserProfileDialog(member.id)}>{member.fullName}</TeamMemberLink>
            </Typography>
          }
        ></ListItemText>
      </ListItem>
    );
  }

  return (
    <MainContainer className={className}>
      <TeamContainer>
        <TeamList>
          <TeamListSubheader>
            <span>Team Leader</span>
          </TeamListSubheader>
          {!leader && (
            <ListItem disableGutters>
              <NoRowsPlaceholder>None</NoRowsPlaceholder>
            </ListItem>
          )}
          {leaderIndividual && renderMember(leaderIndividual)}
        </TeamList>
        <TeamList>
          <TeamListSubheader>
            <span>Team Members</span>
          </TeamListSubheader>
          {(!members || members.length < 1) && (
            <ListItem disableGutters>
              <NoRowsPlaceholder>None</NoRowsPlaceholder>
            </ListItem>
          )}
          {memberIndividuals.map((member) => renderMember(member))}
        </TeamList>
      </TeamContainer>
    </MainContainer>
  );
}
